import React, {FC, useRef, useState, useEffect, ChangeEvent, MouseEvent} from 'react';
import {useTranslation} from 'react-i18next';

import Box from '@mui/material/Box';
import List from '@mui/material/List';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';

import ArrowRightSvgIcon from 'assets/icons/buttons/ArrowRightSvgIcon';

import {MdxEditorCustomButtonType} from 'components/MdxEditorField/helper';
import AutoDirectionTextField from 'components/_common/AutoDirectionTextField';
import {getFilteredItems} from 'components/CodeMirrorField/helper';

import {theme} from 'config/theme';

interface CustomButtonItemsSelectorPopoverType {
    anchorEl: null | HTMLElement;
    setAnchorEl: (value: null | HTMLElement) => void;
    open: boolean;
    items?: MdxEditorCustomButtonType[];
    onOptionSelect: (value: string) => void;
    hasSearchByCustomButtonOptions?: boolean;
}

const CustomButtonItemsSelectorPopover: FC<CustomButtonItemsSelectorPopoverType> = ({
    anchorEl,
    setAnchorEl,
    open,
    items,
    onOptionSelect,
    hasSearchByCustomButtonOptions,
}) => {
    const [t] = useTranslation();

    const fieldInput = useRef<HTMLInputElement | null>(null);

    const [search, setSearch] = useState<string | null>(null);

    const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        const text = event.target.value;
        setSearch(text);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleChange = (event: MouseEvent<HTMLElement>, value: string) => {
        onOptionSelect(value);
        setAnchorEl(null);
    };

    useEffect(() => {
        setTimeout(() => {
            if (open && hasSearchByCustomButtonOptions && fieldInput && fieldInput.current) {
                fieldInput.current.focus();
            }
        }, 500);
    }, [open]);

    const filteredItems = items ? getFilteredItems(items, search, hasSearchByCustomButtonOptions) : [];

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'center',
                horizontal: 'left',
            }}
            PaperProps={{
                elevation: 0,
                sx: {
                    ml: 1,
                    minWidth: 200,
                    minHeight: 60,
                    borderRadius: 4,
                    overflow: 'visible',
                    boxShadow: `0px 0px 16px rgba(0, 0, 0, 0.1)`,
                    backgroundColor: theme.palette.background.default,
                },
            }}
        >
            {hasSearchByCustomButtonOptions && (
                <Box sx={{backgroundColor: theme.palette.warning.contrastText, p: 2}}>
                    <AutoDirectionTextField
                        fullWidth
                        inputRef={fieldInput}
                        variant="standard"
                        value={search}
                        onChange={onSearchChange}
                    />
                </Box>
            )}
            <List sx={{p: 1, backgroundColor: theme.palette.background.default}}>
                {filteredItems &&
                    filteredItems.map((option: MdxEditorCustomButtonType, index: number) => {
                        const {title, markdown, isSelector, isSubItem} = option;
                        return (
                            <MenuItem key={`option-${index}`} onClick={e => handleChange(e, markdown)}>
                                {isSubItem && <ArrowRightSvgIcon />}
                                <Typography
                                    sx={{ml: isSubItem ? 1 : 'initial', fontWeight: isSelector ? 700 : 'initial'}}
                                >
                                    {t(title)}
                                </Typography>
                            </MenuItem>
                        );
                    })}
            </List>
        </Popover>
    );
};

export default CustomButtonItemsSelectorPopover;
